import { motion, useReducedMotion } from 'framer-motion';
import { PlayCircle, Clock } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { interpolate } from '../../utils/cabinI18n';

export default function ContinueLessonCard({ lesson, onContinue }) {
  const { t } = useLanguage();
  const shouldReduce = useReducedMotion();

  const cardVariants = {
    initial: { scale: 1, y: 0 },
    hover: shouldReduce ? {} : { scale: 1.01, y: -1 },
    tap: shouldReduce ? {} : { scale: 0.99 },
  };

  if (!lesson) {
    return (
      <div
        className="bg-white rounded-2xl p-6 border-2 border-gray-200 shadow-[0_4px_0_0_#E5E5E5] flex flex-col items-center gap-3 text-center"
        data-testid="cabin-empty-continue-card"
      >
        <PlayCircle size={40} className="text-[var(--brand-primary)]" aria-hidden="true" />
        <h3 className="text-lg font-black text-[var(--text-main)]">
          {t('cabin.empty.title')}
        </h3>
        <p className="text-sm text-[var(--text-muted)]">{t('cabin.empty.sub')}</p>
        <motion.button
          variants={cardVariants}
          whileHover="hover"
          whileTap="tap"
          onClick={onContinue}
          className="mt-1 px-6 py-2.5 bg-[var(--brand-primary)] text-white font-bold rounded-xl shadow-[0_4px_0_0_var(--brand-primary-shadow)] hover:bg-[var(--brand-primary-hover)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
          aria-label={t('cabin.empty.cta')}
          data-testid="cabin-empty-cta"
        >
          {t('cabin.empty.cta')}
        </motion.button>
      </div>
    );
  }

  return (
    <div
      className="bg-white rounded-2xl p-6 border-2 border-gray-200 shadow-[0_4px_0_0_#E5E5E5]"
      data-testid="cabin-dashboard-hero"
    >
      <div className="flex items-center gap-2 mb-4">
        <PlayCircle size={22} className="text-[var(--brand-primary)]" aria-hidden="true" />
        <h2 className="text-base font-black text-[var(--text-main)]">
          {t('cabin.continue.title')}
        </h2>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[var(--text-main)] truncate">
            {lesson.title}
          </p>
          <p className="text-xs text-[var(--text-muted)] mt-0.5">{lesson.breadcrumb}</p>

          {/* Progress bar */}
          <div className="mt-3 h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-[var(--brand-primary)] rounded-full transition-all duration-500"
              style={{ width: `${lesson.progress}%` }}
            />
          </div>
          <p className="text-xs text-[var(--text-muted)] mt-1">{lesson.progress}%</p>

          {lesson.remainingMin && (
            <div className="flex items-center gap-1 mt-1">
              <Clock size={12} className="text-[var(--text-muted)]" aria-hidden="true" />
              <span className="text-xs text-[var(--text-muted)]">
                {interpolate(t('cabin.continue.remaining'), { min: lesson.remainingMin })}
              </span>
            </div>
          )}
        </div>

        <motion.button
          variants={cardVariants}
          whileHover="hover"
          whileTap="tap"
          onClick={onContinue}
          className="shrink-0 px-6 py-2.5 bg-[var(--brand-primary)] text-white font-bold rounded-xl shadow-[0_4px_0_0_var(--brand-primary-shadow)] hover:bg-[var(--brand-primary-hover)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] flex items-center gap-1.5"
          aria-label={t('cabin.module.cta.continue')}
          data-testid="cabin-continue-btn"
        >
          {t('cabin.module.cta.continue')} →
        </motion.button>
      </div>
    </div>
  );
}
